// web/src/pages/BetHistoryPage.jsx
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBetSlip } from '../context/BetSlipContext';
import { AuthContext } from '../context/AuthContext';
import { useBTCPrice } from '../hooks/useBTCPrice';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { PageLoader } from '../components/common/Loading';
import {
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiCalendar,
  FiDollarSign,
  FiChevronDown,
  FiChevronUp,
  FiTrendingUp,
  FiCpu,
  FiEye,
  FiEyeOff
} from 'react-icons/fi';
import { FaBitcoin } from 'react-icons/fa';

const BetHistoryPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const { getBetHistory } = useBetSlip();
  
  // Use BTC Price Hook
  const { 
    price: btcPrice, 
    formattedPrice, 
    btcToUsd 
  } = useBTCPrice(60000);
  
  const [loading, setLoading] = useState(true);
  const [bets, setBets] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [expandedBet, setExpandedBet] = useState(null);
  const [displayCurrency, setDisplayCurrency] = useState('usd'); // 'usd' or 'btc'
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchBetHistory();
  }, [user, filter, pagination.page]);

  const fetchBetHistory = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        status: filter === 'all' ? null : filter
      };
      
      const result = await getBetHistory(params);
      
      if (result.success) {
        const data = result.data;
        if (data && data.bets) {
          setBets(data.bets);
        } else if (data && data.data) {
          setBets(data.data);
        } else if (Array.isArray(data)) {
          setBets(data);
        } else {
          setBets([]);
        }
        
        if (data.pagination) {
          setPagination(data.pagination);
        } else if (data.meta) {
          setPagination({
            page: data.meta.current_page || 1,
            limit: data.meta.per_page || 20,
            total: data.meta.total || 0,
            pages: data.meta.last_page || 0
          });
        }
        
        if (data.statistics) {
          setStats(data.statistics);
        }
      } else {
        setBets([]);
      }
    } catch (error) {
      setBets([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (betId) => {
    setExpandedBet(expandedBet === betId ? null : betId);
  };

  // Toggle currency display
  const toggleCurrency = () => {
    setDisplayCurrency(prev => prev === 'usd' ? 'btc' : 'usd');
  };

  // === Formatting Helpers ===
  const formatBTC = (amount) => {
    if (amount === undefined || amount === null) return '0.00000000';
    return parseFloat(amount).toFixed(8);
  };

  const formatBTCWithLabel = (amount) => {
    if (amount === undefined || amount === null) return '0.00000000 BTC';
    return `${parseFloat(amount).toFixed(8)} BTC`;
  };

  const formatUSD = (btcAmount) => {
    if (btcAmount === undefined || btcAmount === null) return '$0.00';
    const usdAmount = btcToUsd(parseFloat(btcAmount));
    return `$${usdAmount.toFixed(2)}`;
  };

  const formatUSDWithBTC = (btcAmount) => {
    if (btcAmount === undefined || btcAmount === null) return '$0.00 (0.00000000 BTC)';
    const usdAmount = btcToUsd(parseFloat(btcAmount));
    return `$${usdAmount.toFixed(2)} (≈${formatBTC(btcAmount)} BTC)`;
  };

  const getDisplayAmount = (btcAmount) => {
    if (btcAmount === undefined || btcAmount === null) {
      return displayCurrency === 'usd' ? '$0.00' : '0.00000000 BTC';
    }
    if (displayCurrency === 'usd') {
      return formatUSD(btcAmount);
    }
    return formatBTCWithLabel(btcAmount);
  };

  const getDisplayAmountWithBTC = (btcAmount) => {
    if (btcAmount === undefined || btcAmount === null) {
      return displayCurrency === 'usd' ? '$0.00 (0.00000000 BTC)' : '0.00000000 BTC ($0.00)';
    }
    if (displayCurrency === 'usd') {
      return formatUSDWithBTC(btcAmount);
    }
    return `${formatBTCWithLabel(btcAmount)} (≈${formatUSD(btcAmount)})`;
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { 
        icon: <FiClock className="w-4 h-4" />, 
        color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
        label: 'Pending'
      },
      won: { 
        icon: <FiCheckCircle className="w-4 h-4" />, 
        color: 'bg-green-500/20 text-green-400 border-green-500/30',
        label: 'Won 🎉'
      },
      lost: { 
        icon: <FiXCircle className="w-4 h-4" />, 
        color: 'bg-red-500/20 text-red-400 border-red-500/30',
        label: 'Lost'
      },
      cancelled: { 
        icon: <FiAlertCircle className="w-4 h-4" />, 
        color: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
        label: 'Cancelled'
      }
    };
    
    const configItem = config[status] || config.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${configItem.color} flex items-center gap-1.5 inline-flex`}>
        {configItem.icon}
        {configItem.label}
      </span>
    );
  };

  const getMatchStatusBadge = (status) => {
    const config = {
      'NS': { color: 'bg-blue-500/20 text-blue-400', label: 'Not Started' },
      '1H': { color: 'bg-yellow-500/20 text-yellow-400', label: 'Live 🔴' },
      'HT': { color: 'bg-yellow-500/20 text-yellow-400', label: 'Half Time' },
      '2H': { color: 'bg-yellow-500/20 text-yellow-400', label: 'Live 🔴' },
      'FT': { color: 'bg-gray-500/20 text-gray-400', label: 'Finished' },
      'AET': { color: 'bg-gray-500/20 text-gray-400', label: 'After ET' },
      'PEN': { color: 'bg-gray-500/20 text-gray-400', label: 'Penalties' }
    };
    
    const configItem = config[status] || { color: 'bg-gray-500/20 text-gray-400', label: status };
    return (
      <span className={`px-2 py-0.5 rounded text-xs ${configItem.color}`}>
        {configItem.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Stats Cards with USD
  const statCards = [
    {
      title: 'Total Bets',
      value: stats?.total_bets || 0,
      icon: <FiClock className="w-5 h-5" />,
      color: 'from-blue-500 to-cyan-600'
    },
    {
      title: 'Won',
      value: stats?.won_bets || 0,
      icon: <FiCheckCircle className="w-5 h-5" />,
      color: 'from-green-500 to-emerald-600'
    },
    {
      title: 'Lost',
      value: stats?.lost_bets || 0,
      icon: <FiXCircle className="w-5 h-5" />,
      color: 'from-red-500 to-rose-600'
    },
    {
      title: 'Total Winnings',
      value: getDisplayAmount(stats?.total_winnings || 0),
      icon: <FiTrendingUp className="w-5 h-5" />,
      color: 'from-yellow-500 to-amber-600'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-deep-black pt-16">
        <Header />
        <PageLoader text="Loading bet history..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-black pt-16">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">📋 Bet History</h1>
            <p className="text-gray-400 text-sm">View all your betting activity</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Currency Toggle */}
            <button
              onClick={toggleCurrency}
              className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors"
            >
              {displayCurrency === 'usd' ? (
                <>
                  <FiDollarSign className="w-4 h-4 text-green-400" />
                  <span className="text-white">USD</span>
                </>
              ) : (
                <>
                  <FaBitcoin className="w-4 h-4 text-yellow-500" />
                  <span className="text-white">BTC</span>
                </>
              )}
            </button>
            
            <button
              onClick={() => fetchBetHistory()}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-white transition"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* BTC Price Display */}
        {btcPrice && (
          <div className="bg-gray-800 rounded-lg p-2 mb-4 text-center">
            <span className="text-gray-400 text-sm">
              1 BTC = {formattedPrice}
            </span>
          </div>
        )}

        {/* Statistics Cards - Updated with USD */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {statCards.map((card, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${card.color} p-4 rounded-xl shadow-lg`}
            >
              <div className="flex items-center justify-between">
                <div className="text-white/80">{card.icon}</div>
                <div className="text-white/60 text-xs">STAT</div>
              </div>
              <div className="mt-2">
                <div className="text-2xl font-bold text-white">{card.value}</div>
                <div className="text-sm text-white/80">{card.title}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-gray-900 rounded-xl p-4 mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg text-sm transition ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg text-sm transition ${
              filter === 'pending'
                ? 'bg-yellow-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setFilter('won')}
            className={`px-4 py-2 rounded-lg text-sm transition ${
              filter === 'won'
                ? 'bg-green-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            Won 🎉
          </button>
          <button
            onClick={() => setFilter('lost')}
            className={`px-4 py-2 rounded-lg text-sm transition ${
              filter === 'lost'
                ? 'bg-red-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            Lost
          </button>
          <button
            onClick={() => setFilter('cancelled')}
            className={`px-4 py-2 rounded-lg text-sm transition ${
              filter === 'cancelled'
                ? 'bg-gray-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            Cancelled
          </button>
        </div>

        {/* Bet List */}
        {bets.length === 0 ? (
          <div className="bg-gray-900 rounded-xl p-12 text-center">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-xl font-bold text-white mb-2">No bets found</h3>
            <p className="text-gray-400 mb-6">
              {filter !== 'all' ? 'Try changing your filter' : 'Place your first bet to get started'}
            </p>
            {filter !== 'all' ? (
              <button
                onClick={() => setFilter('pending')}
                className="px-6 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition"
              >
                View All Bets
              </button>
            ) : (
              <button
                onClick={() => navigate('/sports')}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg font-bold transition"
              >
                Browse Sports
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {bets.map((bet) => {
              const isAccumulator = bet.is_accumulator || false;
              const selections = bet.selections || [];
              const totalOdds = bet.total_odds || bet.odds || 1;
              const stakeBTC = parseFloat(bet.stake || 0);
              const potentialWinBTC = parseFloat(bet.potential_win || 0);
              
              return (
                <div
                  key={bet.id}
                  className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden hover:border-gray-700 transition"
                >
                  {/* Bet Header */}
                  <div className="p-4 cursor-pointer" onClick={() => toggleExpand(bet.id)}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          {/* Bet Type Badge */}
                          {isAccumulator ? (
                            <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs font-medium flex items-center gap-1">
                              <FiCpu className="w-3 h-3" />
                              Accumulator
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs font-medium">
                              Single
                            </span>
                          )}
                          
                          {/* Status */}
                          {getStatusBadge(bet.status)}
                          
                          {/* Result */}
                          {bet.result && (
                            <span className={`text-xs font-medium ${
                              bet.result === 'won' ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {bet.result === 'won' ? '✅ Won' : '❌ Lost'}
                            </span>
                          )}
                        </div>
                        
                        {/* Match Info - For Single Bets */}
                        {!isAccumulator && (
                          <div className="mt-2">
                            <div className="text-white font-medium">
                              {bet.home_team || 'Home'} vs {bet.away_team || 'Away'}
                            </div>
                            <div className="flex items-center gap-3 mt-1 flex-wrap">
                              <span className="text-sm text-gray-400">
                                {bet.selection_name || bet.selection || '—'}
                              </span>
                              <span className="text-sm text-yellow-400">
                                Odds: {parseFloat(bet.odds || 0).toFixed(2)}x
                              </span>
                              {bet.match_status && (
                                <span className="text-sm">
                                  {getMatchStatusBadge(bet.match_status)}
                                </span>
                              )}
                              {bet.fixture_date && (
                                <span className="text-xs text-gray-500">
                                  {formatDate(bet.fixture_date)}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Accumulator Info */}
                        {isAccumulator && (
                          <div className="mt-2">
                            <div className="text-white font-medium">
                              {selections.length} Selections
                            </div>
                            <div className="flex items-center gap-3 mt-1 flex-wrap">
                              <span className="text-sm text-yellow-400">
                                Total Odds: {parseFloat(totalOdds).toFixed(2)}x
                              </span>
                              <span className="text-xs text-gray-500">
                                Placed: {formatDate(bet.created_at)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Amounts - USD Primary */}
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm text-gray-400">Stake</div>
                        <div className="text-white font-bold">
                          {getDisplayAmount(stakeBTC)}
                        </div>
                        {displayCurrency === 'usd' && (
                          <div className="text-xs text-gray-500">
                            {formatBTCWithLabel(stakeBTC)}
                          </div>
                        )}
                        
                        {bet.potential_win && (
                          <>
                            <div className="text-sm text-gray-400 mt-1">Potential Win</div>
                            <div className="text-green-400 font-bold">
                              {getDisplayAmount(potentialWinBTC)}
                            </div>
                            {displayCurrency === 'usd' && (
                              <div className="text-xs text-gray-500">
                                {formatBTCWithLabel(potentialWinBTC)}
                              </div>
                            )}
                          </>
                        )}
                        {bet.status === 'won' && (
                          <div className="text-xs text-green-400 mt-1">
                            +{getDisplayAmount(potentialWinBTC)}
                          </div>
                        )}
                      </div>
                      
                      {/* Expand Icon */}
                      <div className="text-gray-500 flex-shrink-0">
                        {expandedBet === bet.id ? (
                          <FiChevronUp className="w-5 h-5" />
                        ) : (
                          <FiChevronDown className="w-5 h-5" />
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Content */}
                  {expandedBet === bet.id && (
                    <div className="px-4 pb-4 border-t border-gray-800 pt-4">
                      {/* For Accumulator - Show all selections */}
                      {isAccumulator && selections.length > 0 && (
                        <div className="space-y-3">
                          <div className="text-sm font-medium text-gray-400 mb-2">
                            📋 All Selections
                          </div>
                          {selections.map((selection, index) => {
                            const fixture = selection.fixture_details || {};
                            return (
                              <div key={index} className="bg-gray-800/50 rounded-lg p-3">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                  <div>
                                    <div className="text-sm text-white font-medium">
                                      {fixture.home_team || selection.home_team || 'Home'} vs {fixture.away_team || selection.away_team || 'Away'}
                                    </div>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className="text-xs text-gray-400">
                                        {selection.selection_name || selection.selection || '—'}
                                      </span>
                                      <span className="text-xs text-yellow-400">
                                        Odds: {parseFloat(selection.odds || 0).toFixed(2)}x
                                      </span>
                                      {fixture.status && (
                                        <span className="text-xs">
                                          {getMatchStatusBadge(fixture.status)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {/* Score */}
                                  {fixture.status === 'FT' && (
                                    <div className="text-sm text-white font-bold">
                                      {fixture.home_score || 0} - {fixture.away_score || 0}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          
                          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-400">Combined Odds:</span>
                              <span className="text-yellow-400 font-bold">
                                {parseFloat(totalOdds).toFixed(2)}x
                              </span>
                            </div>
                            <div className="flex justify-between items-center mt-1">
                              <span className="text-sm text-gray-400">Total Stake:</span>
                              <span className="text-white font-bold">
                                {getDisplayAmount(stakeBTC)}
                              </span>
                            </div>
                            <div className="flex justify-between items-center mt-1">
                              <span className="text-sm text-gray-400">Potential Win:</span>
                              <span className="text-green-400 font-bold">
                                {getDisplayAmount(potentialWinBTC)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* For Single Bets - Show match details */}
                      {!isAccumulator && (
                        <div className="space-y-2">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <span className="text-xs text-gray-400">Bet Type</span>
                              <div className="text-sm text-white">{bet.bet_type || 'Match Winner'}</div>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400">Selection</span>
                              <div className="text-sm text-white">{bet.selection_name || bet.selection}</div>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400">Odds</span>
                              <div className="text-sm text-yellow-400">{parseFloat(bet.odds || 0).toFixed(2)}x</div>
                            </div>
                            <div>
                              <span className="text-xs text-gray-400">Placed</span>
                              <div className="text-sm text-white">{formatDate(bet.created_at)}</div>
                            </div>
                            {bet.settled_at && (
                              <div>
                                <span className="text-xs text-gray-400">Settled</span>
                                <div className="text-sm text-white">{formatDate(bet.settled_at)}</div>
                              </div>
                            )}
                            {bet.bitcoin_tx_hash && (
                              <div>
                                <span className="text-xs text-gray-400">TX Hash</span>
                                <div className="text-sm text-blue-400 truncate max-w-[200px]">
                                  {bet.bitcoin_tx_hash.substring(0, 20)}...
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Amounts Summary */}
                          <div className="grid grid-cols-2 gap-4 mt-3 p-3 bg-gray-800/50 rounded-lg">
                            <div>
                              <div className="text-xs text-gray-400">Stake</div>
                              <div className="text-white font-bold">
                                {getDisplayAmount(stakeBTC)}
                              </div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-400">Potential Win</div>
                              <div className="text-green-400 font-bold">
                                {getDisplayAmount(potentialWinBTC)}
                              </div>
                            </div>
                          </div>
                          
                          {bet.result && (
                            <div className={`p-2 rounded-lg ${
                              bet.result === 'won' ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'
                            }`}>
                              <span className="text-sm font-medium">
                                {bet.result === 'won' ? '✅ Won!' : '❌ Lost'}
                              </span>
                              {bet.result === 'won' && (
                                <span className="text-sm text-green-400 ml-2">
                                  +{getDisplayAmount(potentialWinBTC)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
              disabled={pagination.page <= 1}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              Previous
            </button>
            <span className="px-4 py-2 bg-gray-800 rounded-lg text-white">
              {pagination.page} / {pagination.pages}
            </span>
            <button
              onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
              disabled={pagination.page >= pagination.pages}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              Next
            </button>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default BetHistoryPage;